import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { isKarkhanaSupervisor } from "../../helpers/karkhanaEnabled";
import { useCashbookStatus } from "../../helpers/cashbookStatus";
import {
  getApprovals,
  getKarkhanaDashboard,
  getNotCounted,
  listKarkhanaDesigns,
  listKarkhanaWorkTypes,
  listRateCards,
  listSarees,
  listSupervisors,
  listWorkerCategories,
} from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { LangToggle, useKT } from "./i18n/kt";
import { rupees } from "./rates/rateModel";
import "./home/karkhana-home.scss";
import "./shell/karkhana-more.scss";
import InstallKarkhanaApp from "./supervisor/InstallKarkhanaApp";
import { logoutThisPhone } from "./supervisor/supervisorSession";
import { confirmUnsavedLogout } from "./daily/confirmUnsavedLogout";
import "./supervisor/supervisor-login.scss";
import { kp } from "./karkhanaPaths";
import { OutLink } from "./ui/leaveKarkhana";
import PageHeader from "./ui/PageHeader";
import { myCashInHand, supervisorsCash } from "./cashbook/cashFigures";

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

/** The supervisor's own short list: their cash, what's waiting, language, logout. */
const SupervisorMore = () => {
  const { t } = useKT();
  const history = useHistory();
  const auth = readAuthUser();
  const name: string = auth?.users?.name || "";
  const studio: string = auth?.users?.organizationResponse?.name || "";
  const [cash, setCash] = useState<string | null>(null);
  const [late, setLate] = useState(0);
  // With the Cash book add-on the cash book is its own tab (नकद) and the loom board moves here.
  const cashbook = useCashbookStatus();
  const cashTab = cashbook.loaded && cashbook.enabled;

  // Cash in hand: the cash book's own figure with the add-on (same as the नकद tab), else today's book.
  useEffect(() => {
    if (!cashbook.loaded) return;
    let live = true;
    myCashInHand(cashTab).then((v) => live && setCash(v === null ? null : String(v)));
    return () => {
      live = false;
    };
  }, [cashbook.loaded, cashTab]);

  useEffect(() => {
    getApprovals()
      .then((res: any) => {
        const d = res?.success ? res.data : null;
        setLate(d ? (d.entries?.length || 0) + (d.closures?.length || 0) : 0);
      })
      .catch(() => setLate(0));
  }, []);

  const logout = async () => {
    // Unsaved Daily work on this phone is the user's own: say so before it goes with the logout.
    if (!(await confirmUnsavedLogout())) return;
    logoutThisPhone();
    history.push(kp.login({ studio }));
  };

  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

  return (
    <div className="gi-registry-page kw-page kh-home">
      <PageHeader title={t("More")}>
        <LangToggle />
      </PageHeader>
      <div className="km-me">
        <span className="km-me__avatar" aria-hidden="true">
          {initials || "S"}
        </span>
        <div>
          <strong>{name}</strong>
          <span>
            {t("Supervisor")} · {studio}
          </span>
        </div>
      </div>
      <div className="km-list">
        {cashTab ? (
          <Link to={kp.home()}>
            <span>
              <strong>{t("Home")}</strong>
              <small>{t("Looms")}</small>
            </span>
            <i aria-hidden="true">›</i>
          </Link>
        ) : (
          <Link to={kp.cashBook()}>
            <span>
              <strong>{t("Cash book")}</strong>
              <small>{cash !== null ? t("With you: {{amount}}", { amount: rupees(cash) }) : t("Cash given to you and paid out")}</small>
            </span>
            <i aria-hidden="true">›</i>
          </Link>
        )}
        <Link to={kp.approvals()}>
          <span>
            <strong>{t("Waiting")}</strong>
            <small>{late ? t("{{n}} entries · the owner will check", { n: late }) : t("Nothing waiting")}</small>
          </span>
          {late ? <em className="km-badge">{late}</em> : <i aria-hidden="true">›</i>}
        </Link>
        <Link to={kp.paySheets()}>
          <span>
            <strong>{t("Pay sheets")}</strong>
            <small>{t("Print this week's sheet")}</small>
          </span>
          <i aria-hidden="true">›</i>
        </Link>
        <Link to={kp.sarees()}>
          <span>
            <strong>{t("Sarees")}</strong>
            <small>{t("Saree numbers and history")}</small>
          </span>
          <i aria-hidden="true">›</i>
        </Link>
      </div>
      <div className="ksl__app">
        <InstallKarkhanaApp />
      </div>
      <button type="button" className="km-logout" onClick={logout}>
        {t("Log out")}
      </button>
      <p className="km-note">{t("After logging out, this supervisor login opens again.")}</p>
    </div>
  );
};

type Counts = {
  late: number;
  rateDesigns: number;
  ratePeople: number;
  sarees: number | null;
  cashOut: number | null;
  workers: number | null;
  supervisors: number | null;
  prefixes: string[];
  designs: number | null;
  approvedCards: number | null;
  parts: string[];
};

const EMPTY: Counts = { late: 0, rateDesigns: 0, ratePeople: 0, sarees: null, cashOut: null, workers: null, supervisors: null, prefixes: [], designs: null, approvedCards: null, parts: [] };

const ok = (r: PromiseSettledResult<any>) => (r.status === "fulfilled" && r.value?.success ? r.value.data : null);
const few = (xs: string[]) => (xs.length > 3 ? `${xs.slice(0, 3).join(", ")}…` : xs.join(", "));

/** Live numbers for the More tiles, each from the list it opens. A failed call just shows no number. */
const loadCounts = async (cashbookOn: boolean): Promise<Counts> => {
  const [appr, waiting, sarees, cash, dash, sups, cats, designs, cards, types] = await Promise.allSettled([
    getApprovals(),
    getNotCounted(),
    listSarees(),
    supervisorsCash(cashbookOn),
    getKarkhanaDashboard(),
    listSupervisors(),
    listWorkerCategories(),
    listKarkhanaDesigns({ offset: 0, limit: 1 }),
    listRateCards({ offset: 0, limit: 200 }),
    listKarkhanaWorkTypes(0, 200),
  ]);
  const a = ok(appr);
  const w = ok(waiting);
  const sareeList = ok(sarees);
  const book = cash.status === "fulfilled" ? cash.value : null;
  const d = ok(dash);
  const supList = ok(sups);
  const catList = ok(cats);
  const designPage = ok(designs);
  const cardPage = ok(cards);
  const typePage = ok(types);
  return {
    late: a ? (a.entries?.length || 0) + (a.closures?.length || 0) : 0,
    rateDesigns: w ? new Set((w.loom || []).map((g: any) => g.designId)).size : 0,
    ratePeople: w ? (w.time || []).length : 0,
    sarees: Array.isArray(sareeList) ? sareeList.length : null,
    cashOut: book ? book.reduce((n, x) => n + (Number(x.cashInHand) || 0), 0) : null,
    workers: d?.people?.totals?.workers ?? null,
    supervisors: Array.isArray(supList) ? supList.filter((x: any) => x.active !== false).length : null,
    prefixes: Array.isArray(catList) ? catList.filter((c: any) => c.active !== false).map((c: any) => `${c.prefix}_`) : [],
    designs: designPage ? designPage.length ?? null : null,
    approvedCards: cardPage ? (cardPage.docs || []).filter((c: any) => c.current).length : null,
    parts: typePage ? (typePage.docs || []).filter((x: any) => x.active !== false && /^S_/.test(x.code)).map((x: any) => x.code) : [],
  };
};

type Tile = { to: string; label: string; hint: string; warn?: boolean };

/**
 * Everything that isn't entered every day, grouped by purpose, each tile with a live number
 * (Figma 7a). Owners also get All of HKP here: on phones the app's own menu bar is hidden.
 */
const KarkhanaMorePage = () => {
  const { t } = useKT();
  const supervisor = isKarkhanaSupervisor(readAuthUser()?.users?.role);
  const [c, setC] = useState<Counts>(EMPTY);
  const cashbook = useCashbookStatus();
  const toCheck = cashbook.loaded && cashbook.enabled ? cashbook.badge.toCheck : 0;

  const cashOn = cashbook.loaded && cashbook.enabled;
  useEffect(() => {
    if (supervisor || !cashbook.loaded) return;
    let live = true;
    loadCounts(cashOn).then((x) => live && setC(x));
    return () => {
      live = false;
    };
  }, [supervisor, cashbook.loaded, cashOn]);

  if (supervisor) return <SupervisorMore />;

  const n = (v: number | null, one: string, many: string) => (v === null ? "" : t(v === 1 ? one : many, { n: v }));
  const waitingAny = c.late > 0 || c.rateDesigns + c.ratePeople > 0;
  const groups: { title: string; tiles: Tile[] }[] = [
    {
      title: waitingAny ? "Waiting for you" : "Sarees and checks",
      tiles: [
        { to: kp.approvals(), label: "Approvals", hint: c.late ? n(c.late, "1 late entry", "{{n}} late entries") : t("Nothing late"), warn: c.late > 0 },
        {
          to: kp.pay(undefined, "not-counted"),
          label: "Fill rates",
          hint: c.rateDesigns ? n(c.rateDesigns, "1 design", "{{n}} designs") : c.ratePeople ? n(c.ratePeople, "1 person", "{{n}} people") : t("All rated"),
          warn: c.rateDesigns + c.ratePeople > 0,
        },
        { to: kp.sarees(), label: "Sarees", hint: c.sarees === null ? t("Every saree") : t("All {{n}}", { n: c.sarees }) },
      ],
    },
    {
      title: "Money",
      tiles: [
        { to: kp.paySheets(), label: "Pay sheets", hint: t("Weekly, print") },
        toCheck > 0
          ? { to: kp.cashBook(), label: "Cash book", hint: t("{{n}} to check", { n: toCheck }), warn: true }
          : { to: kp.cashBook(), label: "Cash book", hint: c.cashOut === null ? t("Cash with supervisors") : t("{{amount}} out", { amount: rupees(c.cashOut, 0) }) },
        { to: kp.costings(), label: "Costing", hint: t("Owner only") },
      ],
    },
    {
      title: "People",
      tiles: [
        { to: kp.workers(), label: "Workers", hint: c.workers === null ? t("Codes, KYC") : n(c.workers, "1 person", "{{n}} people") },
        { to: kp.supervisors(), label: "Supervisors", hint: c.supervisors === null ? t("Phone logins") : n(c.supervisors, "1 login", "{{n}} logins") },
        { to: kp.codes(), label: "Codes", hint: c.prefixes.length ? few(c.prefixes) : t("K_, J_, D_…") },
      ],
    },
    {
      title: "Designs & rates",
      tiles: [
        { to: kp.designs(), label: "Designs", hint: c.designs === null ? t("Design numbers") : String(c.designs) },
        { to: kp.rateCards(), label: "Rate cards", hint: c.approvedCards === null ? t("Per inch") : t("{{n}} approved", { n: c.approvedCards }) },
        { to: kp.timeRates(), label: "Time rates", hint: t("Per hour/day") },
        { to: kp.parts(), label: "Saree parts", hint: c.parts.length ? few(c.parts) : t("S_1, S_3…") },
        { to: "/looms", label: "Looms", hint: t("Register, geotag") },
        { to: kp.reports(), label: "Reports", hint: t("Excel") },
      ],
    },
    {
      title: "Imports & help",
      tiles: [
        { to: kp.import(), label: "Import", hint: t("Any sheet, any time") },
        { to: kp.workerPhotos(), label: "Photos", hint: t("One photo each") },
        { to: kp.oldSheet(), label: "Old sheet", hint: t("History kept") },
        { to: kp.costingLines(), label: "Costing lines", hint: t("Material prices") },
        { to: kp.guide(), label: "How it works", hint: t("Guide") },
      ],
    },
  ];

  return (
    <div className="gi-registry-page kw-page kh-home">
      <PageHeader title={t("More")}>
        <LangToggle />
      </PageHeader>
      {groups.map((g) => (
        <section key={g.title} className="km-group">
          <h3>{t(g.title)}</h3>
          <div className="km-tiles">
            {g.tiles.map((i) => (
              <OutLink key={i.to} to={i.to} className={`km-tile${i.warn ? " is-warn" : ""}`}>
                <strong>{t(i.label)}</strong>
                <span>{i.hint}</span>
              </OutLink>
            ))}
          </div>
        </section>
      ))}
      <Link to="/dashboard" className="km-all">
        <strong>{t("All of HKP")}</strong>
        <span>{t("Products, catalogues, certificates")}</span>
        <i aria-hidden="true">›</i>
      </Link>
    </div>
  );
};

export default KarkhanaMorePage;
